import { COUNTRIES } from '@vrplatform/ui-common';
import { useMemo } from 'react';
import { InputSelect } from '../InputSelect';
import type { SelectItem } from '../../select/_types';

type Props = {
  disabled?: boolean;
  value: string | null;
  setValue: (value: SelectItem | null) => void;
  error?: string | boolean;
  placeholder?: string;
  clearable?: boolean;
  countries?: string[];
  loadingQuery?: boolean;
  errorQuery?: Error | null;
  dropdownProps?: Parameters<typeof InputSelect>[0]['dropdownProps'];
};

export const useInputCountryOptions = (initial?: Props['countries']) => {
  return useMemo(() => {
    const pinnedCountries = ['US', 'UK', 'FR', 'DE', 'GB'];

    const c = initial?.length
      ? COUNTRIES.filter(({ isoCode }) => initial.includes(isoCode))
      : COUNTRIES;

    const pinned = c
      .filter((country) => pinnedCountries.includes(country.isoCode))
      .map<SelectItem>((country) => ({
        value: country.isoCode,
        label: country.isoCode === 'GB' ? 'United Kingdom' : country.name,
      }));

    const countries = c
      .filter((country) => !pinnedCountries.includes(country.isoCode))
      .map<SelectItem>((country) => ({
        value: country.isoCode,
        label: country.isoCode === 'GB' ? 'United Kingdom' : country.name,
      }));

    return {
      countries,
      pinned,
      all: [...pinned, ...countries],
    };
  }, [initial]);
};

export const InputCountry = ({
  disabled,
  setValue,
  value,
  error,
  placeholder = 'Country',
  clearable = true,
  countries,
  loadingQuery,
  errorQuery,
  dropdownProps,
}: Props) => {
  const options = useInputCountryOptions(countries);

  const selected = disabled
    ? null
    : options.all.find(
        (option) => option.value.toLowerCase() === value?.toLowerCase()
      ) || null;

  return (
    <InputSelect
      data={{
        options: options.countries,
        loading: loadingQuery,
        error: errorQuery,
      }}
      pinnedItems={options.pinned}
      type="single"
      value={selected}
      setValue={setValue}
      dropdownProps={{
        width: 'target',
        withinPortal: true,
        zIndex: 400,
        ...dropdownProps,
      }}
      inputProps={{
        disabled,
        placeholder,
        error,
        withClearButton: clearable,
        loadingQuery,
      }}
    />
  );
};
